import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Calendar,
  Shield,
  Sparkles,
  Globe2,
  Award,
  Newspaper,
  IdCard,
  Users,
  BarChart3,
  Lock,
  CheckCircle2,
  FileBadge,
  Check,
  Share2,
  MessageSquare,
  TrendingUp,
  Radio,
  LineChart,
  FileText,
  CalendarClock,
  ThumbsUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MarketingNav } from "@/components/Marketing/MarketingNav";
import { MarketingFooter } from "@/components/Marketing/MarketingFooter";
import { MarketingFinalCta } from "@/components/Marketing/MarketingFinalCta";

import heroImg from "@/assets/hero-press-orbit.jpg";
import bentoRegistry from "@/assets/bento-registry.jpg";
import bentoEvents from "@/assets/bento-events.jpg";
import bentoCoverage from "@/assets/bento-coverage.jpg";


/* ───────────────────────── Hero ───────────────────────── */
function Hero() {
  return (
    <section className="relative pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-emerald opacity-50 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,black,transparent)]" aria-hidden />
      <div className="absolute top-1/3 -left-32 w-[480px] h-[480px] rounded-full bg-primary/10 blur-3xl" aria-hidden />
      <div className="absolute top-1/4 -right-20 w-[420px] h-[420px] rounded-full bg-accent/15 blur-3xl" aria-hidden />

      <div className="container mx-auto px-4 relative">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
          <div className="animate-fade-in">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-accent border border-accent/30 bg-accent/5 px-3 py-1.5 rounded-full">
              <Sparkles className="w-3 h-3" /> Communications backbone
            </span>
            <h1 className="mt-6 font-display text-5xl md:text-6xl xl:text-7xl leading-[1.02] tracking-tight text-balance text-foreground">
              The orbit your{" "}
              <span className="text-gradient-emerald">institutional press</span>{" "}
              has been waiting for.
            </h1>
            <p className="text-justify mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              The PR-operations suite institutions have been missing: schedule and approve
              across every social network, score every piece of coverage, and run press
              accreditation, events and awards, one shared orbit, audit-ready end to end.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/auth?mode=signup">
                <Button size="lg" className="gap-2 h-12 px-7 text-base shadow-glow">
                  Request institution access <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/portal/login">
                <Button size="lg" variant="outline" className="h-12 px-7 text-base border-accent/40 hover:bg-accent/5 hover:text-accent">
                  Journalist portal
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs text-muted-foreground">
              {["Built for institutions", "Unified journalist sign-in", "Audit-ready", "EN · FR · PT"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary-glow" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Hero image card */}
          <div className="relative animate-fade-in">
            <div className="absolute -inset-4 gradient-aurora rounded-3xl blur-2xl opacity-70" aria-hidden />
            <div className="relative border-beam rounded-3xl overflow-hidden bg-card shadow-lg">
              <img
                src={heroImg}
                alt="Modern institutional press hall in emerald and gold lighting"
                className="w-full h-[460px] object-cover"
                width={1536}
                height={1024}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" aria-hidden />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-ivory">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent-soft">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent node-pulse" /> Live press room
                </div>
                <span className="text-[11px] tracking-wider text-ivory/70">v2.6, Orbit</span>
              </div>
            </div>
            {/* Floating stat badges */}
            <div className="hidden md:flex absolute -left-8 top-10 bg-card border border-border rounded-xl px-4 py-3 shadow-md animate-float">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Accredited</div>
                <div className="font-display text-xl text-foreground">12,480</div>
              </div>
            </div>
            <div className="hidden md:flex absolute -right-6 bottom-12 bg-card border border-border rounded-xl px-4 py-3 shadow-md animate-float" style={{ animationDelay: "1.5s" }}>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Coverage score</div>
                <div className="font-display text-xl text-gradient-gold">94.7</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Marquee ───────────────────────── */
function Marquee() {
  const tags = [
    "Parliaments", "Ministries", "Universities", "News agencies",
    "Federations", "Broadcasters", "NGOs", "Regulators", "Embassies",
  ];
  return (
    <section className="relative py-10 border-y border-border bg-muted/40 overflow-hidden">
      <div className="flex animate-marquee gap-12 whitespace-nowrap w-max">
        {[...tags, ...tags].map((t, i) => (
          <span key={i} className="inline-flex items-center gap-3 text-sm font-display text-muted-foreground/80 uppercase tracking-[0.2em]">
            <span className="w-1 h-1 rounded-full bg-accent" />
            {t}
          </span>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Social Suite ───────────────────────── */
function SocialSuite() {
  const features = [
    { icon: MessageSquare, title: "Unified inbox", body: "Mentions, DMs and comments from Facebook, Instagram, X and LinkedIn in one queue." },
    { icon: CalendarClock, title: "Visual scheduler", body: "Drag-and-drop calendar across all four networks with per-network previews." },
    { icon: ThumbsUp, title: "Approval workflows", body: "Draft → review → publish, with role-aware approvers and full audit trail." },
    { icon: Sparkles, title: "AI caption & hashtag studio", body: "Claude-powered copy and hashtag generation, tuned per platform and language." },
    { icon: TrendingUp, title: "Best-time predictions", body: "ML-driven send-time recommendations per audience and channel." },
    { icon: BarChart3, title: "Cross-platform analytics", body: "Reach, engagement, sentiment and growth, unified, not stitched." },
  ];
  return (
    <section id="social" className="py-24 lg:py-32 border-y border-border bg-muted/20">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">Social Suite</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 text-foreground text-balance">
              A <span className="text-gradient-emerald">full-grade</span> social hub, built into your press operation.
            </h2>
            <p className="text-justify text-muted-foreground mt-5 text-lg leading-relaxed max-w-xl">
              Schedule, approve, publish and measure across Facebook, Instagram, X and LinkedIn
              with the same governance, roles and audit logs as the rest of the platform.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {["Facebook", "Instagram", "X / Twitter", "LinkedIn"].map((p) => (
                <span key={p} className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-border bg-card text-foreground/80">
                  <Share2 className="w-3.5 h-3.5 text-accent" /> {p}
                </span>
              ))}
            </div>
          </div>

          {/* Faux scheduler card */}
          <div className="relative">
            <div className="absolute -inset-4 gradient-aurora rounded-3xl blur-2xl opacity-50" aria-hidden />
            <div className="relative rounded-2xl border border-border bg-card p-6 shadow-md">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-accent" />
                  <span className="font-display text-sm">This week's queue</span>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">4 channels · 12 posts</span>
              </div>
              <ul className="space-y-3">
                {[
                  { ch: "LinkedIn",  t: "Wed 09:30", title: "Minister addresses ECOWAS Summit", status: "Approved", color: "text-primary-glow" },
                  { ch: "X",         t: "Wed 11:00", title: "Live: press briefing thread", status: "Scheduled", color: "text-accent" },
                  { ch: "Instagram", t: "Thu 14:15", title: "Backstage carousel, 6 frames", status: "In review", color: "text-yellow-500" },
                  { ch: "Facebook",  t: "Fri 08:00", title: "Weekly roundup + video", status: "Draft", color: "text-muted-foreground" },
                ].map((p) => (
                  <li key={p.title} className="flex items-center gap-4 p-3 rounded-xl border border-border bg-background/60 hover:border-accent/40 transition-colors">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-20 shrink-0">{p.ch}</span>
                    <span className="text-[11px] font-mono text-muted-foreground w-20 shrink-0">{p.t}</span>
                    <span className="text-sm text-foreground flex-1 truncate">{p.title}</span>
                    <span className={`text-[10px] uppercase tracking-wider ${p.color} shrink-0`}>{p.status}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-border grid grid-cols-3 gap-4 text-center">
                {[{ k: "Reach", v: "284K" }, { k: "Engagement", v: "6.4%" }, { k: "Sentiment", v: "+82" }].map((m) => (
                  <div key={m.k}>
                    <div className="font-display text-xl text-foreground">{m.v}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{m.k}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Feature grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-6 hover-scale">
              <f.icon className="w-5 h-5 text-accent mb-3" />
              <h3 className="font-display text-base text-foreground">{f.title}</h3>
              <p className="text-justify text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/social-suite" className="text-sm text-accent hover:underline inline-flex items-center gap-1.5">
            Explore the full Social Suite <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Coverage Intelligence ───────────────────────── */
function CoverageIntel() {
  const pillars = [
    { icon: BarChart3, title: "AI article scoring", body: "Every submission rated on Accuracy, Tone, Reach and Quality, with human override and full audit trail." },
    { icon: Radio,     title: "Mention listening",  body: "Real-time brand and topic monitoring across news sites and social, in EN · FR · PT." },
    { icon: LineChart, title: "Share of voice",     body: "Benchmark your institution's coverage against peers, week over week, channel by channel." },
    { icon: FileText,  title: "Board-ready PR reports", body: "One click to export a polished PDF or CSV your principal can read on a Sunday night." },
  ];
  return (
    <section id="coverage" className="py-24 lg:py-32">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">Coverage Intelligence</span>
          <h2 className="font-display text-4xl md:text-5xl mt-3 text-foreground text-balance">
            The <span className="text-gradient-gold">PR analytics tool</span> the market has been missing.
          </h2>
          <p className="text-justify text-muted-foreground mt-4 text-lg leading-relaxed">
            Visibility you can defend in a board meeting. Sentiment you can act on. Reach
            you can prove. CONORBIT measures every piece of coverage with the rigour your
            institution deserves.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 items-start">
          {/* Mock analytics card */}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-md">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Coverage score · last 30 days</div>
                <div className="font-display text-4xl text-gradient-emerald mt-1">94.7</div>
              </div>
              <span className="text-[11px] text-primary-glow inline-flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5" /> +6.2 vs. prior
              </span>
            </div>
            {/* Sparkline */}
            <svg viewBox="0 0 300 80" className="w-full h-20">
              <polyline
                fill="none"
                stroke="hsl(var(--accent))"
                strokeWidth="2"
                points="0,55 25,48 50,52 75,40 100,44 125,30 150,34 175,22 200,28 225,18 250,24 275,12 300,16"
              />
              <polyline
                fill="hsl(var(--accent) / 0.15)"
                stroke="none"
                points="0,55 25,48 50,52 75,40 100,44 125,30 150,34 175,22 200,28 225,18 250,24 275,12 300,16 300,80 0,80"
              />
            </svg>
            <div className="grid grid-cols-4 gap-3 mt-6">
              {[
                { k: "Accuracy", v: "96" },
                { k: "Tone",     v: "91" },
                { k: "Reach",    v: "88" },
                { k: "Quality",  v: "93" },
              ].map((m) => (
                <div key={m.k} className="rounded-xl border border-border bg-background/60 p-3 text-center">
                  <div className="font-display text-xl text-foreground">{m.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{m.k}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pillars */}
          <div className="grid sm:grid-cols-2 gap-4">
            {pillars.map((p) => (
              <div key={p.title} className="rounded-2xl border border-border bg-card p-6 hover-scale">
                <p.icon className="w-5 h-5 text-accent mb-3" />
                <h3 className="font-display text-base text-foreground">{p.title}</h3>
                <p className="text-justify text-sm text-muted-foreground mt-1.5 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/coverage" className="text-sm text-accent hover:underline inline-flex items-center gap-1.5">
            Explore Coverage Intelligence <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Bento ───────────────────────── */
function Bento() {

  return (
    <section id="product" className="py-24 lg:py-32 relative">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">The product</span>
          <h2 className="font-display text-4xl md:text-5xl mt-3 text-balance text-foreground">
            Six surfaces, one <span className="text-gradient-emerald">orbit</span>.
          </h2>
          <p className="text-justify text-muted-foreground mt-4 text-lg">
            Every module is institution-isolated, role-aware and audit-ready. Designed for
            institutions that take their press operations seriously.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-[180px]">
          {/* A, Media Registry (2x2 image) */}
          <article className="md:col-span-2 lg:row-span-2 relative rounded-2xl overflow-hidden border border-border bg-card hover-scale group">
            <img src={bentoRegistry} alt="Press credentials grid" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/60 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-7 text-ivory">
              <Newspaper className="w-7 h-7 text-accent mb-3" />
              <h3 className="font-display text-2xl">Media Registry</h3>
              <p className="text-justify text-ivory/75 text-sm mt-2 max-w-md">
                A living directory of journalists, outlets and accreditations, searchable,
                versioned, and shared across your institution.
              </p>
            </div>
          </article>

          {/* B, Events (2x1) */}
          <article className="md:col-span-2 lg:col-span-1 lg:row-span-1 rounded-2xl border border-border bg-card p-6 hover-scale relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-accent/10 blur-2xl" />
            <Calendar className="w-6 h-6 text-accent mb-3" />
            <h3 className="font-display text-xl text-foreground">Events &amp; Accreditation</h3>
            <p className="text-justify text-sm text-muted-foreground mt-1.5">
              Run press events end-to-end, invites, requests, passes, attendance codes.
            </p>
          </article>

          {/* C, Press Passes (1x1) */}
          <article className="rounded-2xl border border-border bg-card p-6 hover-scale relative overflow-hidden">
            <IdCard className="w-6 h-6 text-primary-glow mb-3" />
            <h3 className="font-display text-lg text-foreground">Press Passes</h3>
            <p className="text-justify text-xs text-muted-foreground mt-1.5">QR-verifiable, on demand.</p>
            <div className="absolute bottom-3 right-3 text-[10px] font-mono text-accent/70">#PRS-04221</div>
          </article>

          {/* D, Coverage (1x2 image) */}
          <article className="lg:row-span-2 relative rounded-2xl overflow-hidden border border-border bg-card hover-scale group">
            <img src={bentoCoverage} alt="Coverage constellation" className="absolute inset-0 w-full h-full object-cover opacity-80" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black/95" />
            <div className="relative h-full flex flex-col justify-end p-6 text-ivory">
              <BarChart3 className="w-7 h-7 text-accent mb-3" />
              <h3 className="font-display text-2xl">Coverage Intelligence</h3>
              <p className="text-justify text-ivory/75 text-sm mt-2">
                AI-scored articles, fairness signals, journalist contribution maps.
              </p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-4xl text-gradient-gold">94.7</span>
                <span className="text-xs text-ivory/60 uppercase tracking-wider">avg score</span>
              </div>
            </div>
          </article>

          {/* E, Awards (2x1 image) */}
          <article className="md:col-span-2 relative rounded-2xl overflow-hidden border border-border bg-card hover-scale group">
            <img src={bentoEvents} alt="Accreditation desk" className="absolute inset-0 w-full h-full object-cover opacity-90" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/70 to-transparent" />
            <div className="relative h-full flex items-center p-7 text-ivory">
              <div>
                <Award className="w-7 h-7 text-accent mb-2" />
                <h3 className="font-display text-2xl">Awards &amp; Recognition</h3>
                <p className="text-justify text-ivory/75 text-sm mt-1.5 max-w-md">
                  Stage your annual press awards, nominations, jury, scoring, ceremony.
                </p>
              </div>
            </div>
          </article>

          {/* F, Governance (3x1) */}
          <article className="md:col-span-2 lg:col-span-3 relative rounded-2xl border border-border bg-card p-7 hover-scale overflow-hidden">
            <div className="absolute inset-0 bg-dot-emerald opacity-40" />
            <div className="relative flex flex-wrap items-center gap-8 justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center shadow-glow">
                  <Shield className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-display text-xl text-foreground">Multi-institution Governance</h3>
                  <p className="text-justify text-sm text-muted-foreground mt-0.5">Role-aware. Audit-ready. One journalist account across every institution.</p>
                </div>
              </div>
              <div className="flex items-center gap-6 text-xs text-muted-foreground">
                {[
                  { icon: Users, label: "75 tables" },
                  { icon: Lock, label: "RLS enforced" },
                  { icon: FileBadge, label: "ISO-aligned" },
                  { icon: Globe2, label: "EN · FR · PT" },
                ].map(({ icon: Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-2">
                    <Icon className="w-4 h-4 text-accent" /> {label}
                  </span>
                ))}
              </div>
            </div>
          </article>
        </div>

        <div className="mt-10 text-center">
          <Link to="/platform" className="text-sm text-accent hover:underline inline-flex items-center gap-1.5">
            Explore the full Platform <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── How it works ───────────────────────── */
function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Onboard your institution",
      body: "We provision your institution with branding, governance, and your CCO. White-glove migration available.",
    },
    {
      n: "02",
      title: "Open your journalist portal",
      body: "Journalists register once, get KYC'd, request press passes and submit coverage from a single doorway.",
    },
    {
      n: "03",
      title: "Run events, score coverage",
      body: "Invitations, attendance codes, fair selection, post-event coverage scoring and awards, all in one orbit.",
    },
  ];
  return (
    <section className="py-24 lg:py-32 bg-muted/30 border-y border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mb-16">
          <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">How it works</span>
          <h2 className="font-display text-4xl md:text-5xl mt-3 text-foreground">From chaos to <span className="text-gradient-gold">choreography</span>.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.n} className="relative rounded-2xl bg-card border border-border p-8 hover-scale">
              <div className="font-display text-6xl text-gradient-gold leading-none">{s.n}</div>
              <h3 className="font-display text-xl text-foreground mt-5">{s.title}</h3>
              <p className="text-justify text-sm text-muted-foreground mt-3 leading-relaxed">{s.body}</p>
              {i < steps.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-accent/40" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── For Journalists ───────────────────────── */
function ForJournalists() {
  return (
    <section id="journalists" className="py-24 lg:py-32">
      <div className="container mx-auto px-4">
        <div className="relative rounded-3xl overflow-hidden border border-border gradient-emerald-radial text-ivory p-10 md:p-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="absolute inset-0 bg-grid-emerald opacity-30" aria-hidden />
          <div className="relative">
            <span className="text-[11px] uppercase tracking-[0.25em] text-accent">For journalists</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 text-ivory leading-tight">
              Your press credentials,<br />
              <span className="text-gradient-gold">one tap away.</span>
            </h2>
            <p className="text-justify text-ivory/75 mt-5 max-w-md leading-relaxed">
              Sign in once. Hold every accreditation, request, pass and
              submission across all the institutions you cover, in one portal.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/portal/register">
                <Button size="lg" variant="default" className="bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-7">
                  Create journalist account
                </Button>
              </Link>
              <Link to="/portal/login">
                <Button size="lg" variant="outline" className="border-accent/40 text-ivory hover:bg-accent/10 h-12 px-7 bg-transparent">
                  Sign in
                </Button>
              </Link>
            </div>
            <Link to="/journalists" className="mt-5 inline-flex items-center gap-1.5 text-sm text-accent hover:underline">
              Learn more about the journalist portal <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <ul className="relative space-y-4">
            {[
              "One account across every institution you cover",
              "One profile, all your accreditations",
              "Coverage submissions auto-scored",
              "Fair event selection, geo-capped & transparent",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 bg-ivory/5 border border-ivory/10 rounded-xl p-4 backdrop-blur-sm">
                <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <span className="text-ivory/90 text-sm">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Pricing ───────────────────────── */
function Pricing() {
  const highlights = [
    "Plans for solo journalists, single institutions, and large federations",
    "Transparent per-module pricing, no hidden seat fees",
    "Switch or cancel anytime, prorated automatically",
  ];
  return (
    <section id="pricing" className="py-24 lg:py-32 bg-muted/30 border-y border-border">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">Pricing</span>
          <h2 className="font-display text-4xl md:text-5xl mt-3 text-foreground">
            Built for the way <span className="text-gradient-emerald">institutions</span> actually buy.
          </h2>
          <p className="text-justify text-muted-foreground mt-4 text-lg leading-relaxed">
            From a single accredited journalist to a federation of institutions, every plan
            scales with the modules you actually use.
          </p>
          <ul className="mt-7 space-y-2.5 text-left max-w-sm mx-auto">
            {highlights.map((h) => (
              <li key={h} className="flex items-start gap-2.5 text-sm text-foreground/85">
                <CheckCircle2 className="w-4 h-4 text-primary-glow mt-0.5 shrink-0" />
                {h}
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Link to="/pricing">
              <Button size="lg" className="gap-2 h-12 px-7">
                See full plans & pricing <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── FAQ + CTA ───────────────────────── */
function Faq() {
  const items = [
    { q: "Is CONORBIT shared across institutions?", a: "Every institution gets its own fully isolated account with separate data, branding, roles, and audit logs. Row-level security is enforced everywhere." },
    { q: "How do journalists sign in?", a: "With a single email and password. Journalists land in one unified portal across all the institutions they cover." },
    { q: "Which languages are supported?", a: "English, French and Portuguese ship today. Arabic and Swahili are available on request." },
    { q: "Can we self-host?", a: "Federal tier customers can choose on-prem or VPC deployment. Otherwise we host on hardened Lovable Cloud infra." },
  ];
  return (
    <section id="faq" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-accent font-medium">FAQ</span>
          <h2 className="font-display text-4xl md:text-5xl mt-3 text-foreground">Quietly answered.</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((it, i) => (
            <AccordionItem key={i} value={`i-${i}`} className="bg-card border border-border rounded-xl px-5">
              <AccordionTrigger className="text-left font-display text-base hover:no-underline">{it.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{it.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-8 text-center">
          <Link to="/faq" className="text-sm text-accent hover:underline inline-flex items-center gap-1.5">
            Read the full FAQ <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Page ───────────────────────── */
export default function Landing() {
  useTranslation();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />
      <Hero />
      <Marquee />
      <SocialSuite />
      <CoverageIntel />
      <Bento />

      <HowItWorks />
      <ForJournalists />
      <Pricing />
      <Faq />
      <MarketingFinalCta />
      <MarketingFooter />
    </div>
  );
}
